import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import HelpModal from '../components/HelpModal';
import { helpGuides } from '../utils/helpGuides';

const API_URL = process.env.REACT_APP_API_URL || 'https://templimizeai-coursecreatorvault.onrender.com/api';

interface DripCampaign {
  id: number;
  name: string;
  description: string;
  trigger_type: string;
  target_tier: string;
  status: string;
  email_count: number;
  subscriber_count: number;
  created_at: string;
}

interface DripEmail {
  id: number;
  sequence_order: number;
  delay_days: number;
  delay_hours: number;
  subject: string;
  content: string;
  is_active: boolean;
}

const DripCampaigns: React.FC = () => {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState<DripCampaign[]>([]);
  const [selectedCampaign, setSelectedCampaign] = useState<number | null>(null);
  const [campaignEmails, setCampaignEmails] = useState<DripEmail[]>([]);
  const [loading, setLoading] = useState(true);
  const [showNewCampaignModal, setShowNewCampaignModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  // New campaign form
  const [newCampaign, setNewCampaign] = useState({
    name: '',
    description: '',
    triggerType: 'user_signup',
    targetTier: 'all'
  });

  // New email form
  const [newEmail, setNewEmail] = useState({
    sequenceOrder: 1,
    delayDays: 0,
    delayHours: 0,
    subject: '',
    content: ''
  });

  useEffect(() => {
    fetchCampaigns();
  }, []);

  const fetchCampaigns = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/drip-campaigns`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCampaigns(response.data.campaigns);
      setIsAdmin(response.data.isAdmin || false);
      setLoading(false);
    } catch (error: any) {
      console.error('Error fetching campaigns:', error);
      if (error.response?.status === 403 && error.response?.data?.requiresUpgrade) {
        alert(error.response.data.error);
        navigate('/pricing');
      } else if (error.response?.status === 401) {
        navigate('/login');
      }
      setLoading(false);
    }
  };

  const fetchCampaignDetails = async (campaignId: number) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/drip-campaigns/${campaignId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCampaignEmails(response.data.campaign.emails || []);
      setSelectedCampaign(campaignId);
    } catch (error) {
      console.error('Error fetching campaign details:', error);
    }
  };

  const createCampaign = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/drip-campaigns`, newCampaign, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowNewCampaignModal(false);
      setNewCampaign({ name: '', description: '', triggerType: 'user_signup', targetTier: 'all' });
      fetchCampaigns();
    } catch (error) {
      console.error('Error creating campaign:', error);
      alert('Failed to create campaign');
    }
  };

  const addEmailToCampaign = async () => {
    if (!selectedCampaign) return;

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/drip-campaigns/${selectedCampaign}/emails`,
        newEmail,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShowEmailModal(false);
      setNewEmail({ sequenceOrder: 1, delayDays: 0, delayHours: 0, subject: '', content: '' });
      fetchCampaignDetails(selectedCampaign);
    } catch (error) {
      console.error('Error adding email:', error);
      alert('Failed to add email');
    }
  };

  const updateCampaignStatus = async (campaignId: number, status: string) => {
    try {
      const token = localStorage.getItem('token');
      const campaign = campaigns.find(c => c.id === campaignId);
      if (!campaign) return;

      await axios.put(
        `${API_URL}/drip-campaigns/${campaignId}`,
        { ...campaign, status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchCampaigns();
    } catch (error) {
      console.error('Error updating campaign:', error);
      alert('Failed to update campaign');
    }
  };

  const deleteCampaign = async (campaignId: number) => {
    if (!window.confirm('Are you sure you want to delete this campaign?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/drip-campaigns/${campaignId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchCampaigns();
      setSelectedCampaign(null);
    } catch (error) {
      console.error('Error deleting campaign:', error);
      alert('Failed to delete campaign');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading campaigns...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Email Drip Campaigns</h1>
              <p className="mt-1 text-sm text-gray-500">
                Automate your email marketing with behavior-triggered sequences
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowHelpModal(true)}
                className="px-4 py-2 bg-purple-100 text-purple-700 border border-purple-300 rounded-lg hover:bg-purple-200 flex items-center gap-2 font-medium"
                title="How to set up drip campaigns"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Help
              </button>
              <button
                onClick={() => navigate(isAdmin ? '/admin' : '/dashboard')}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                {isAdmin ? 'Back to Admin' : 'Back to Dashboard'}
              </button>
              <button
                onClick={() => setShowNewCampaignModal(true)}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
              >
                + Create Campaign
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Campaigns List */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow">
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">Campaigns ({campaigns.length})</h2>
              </div>
              <div className="divide-y divide-gray-200">
                {campaigns.length === 0 ? (
                  <div className="p-8 text-center text-gray-500">
                    <p>No campaigns yet</p>
                    <button
                      onClick={() => setShowNewCampaignModal(true)}
                      className="mt-4 text-indigo-600 hover:text-indigo-700 font-medium"
                    >
                      Create your first campaign
                    </button>
                  </div>
                ) : (
                  campaigns.map((campaign) => (
                    <div
                      key={campaign.id}
                      onClick={() => fetchCampaignDetails(campaign.id)}
                      className={`p-4 cursor-pointer hover:bg-gray-50 ${
                        selectedCampaign === campaign.id ? 'bg-indigo-50' : ''
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h3 className="font-medium text-gray-900">{campaign.name}</h3>
                          <p className="text-sm text-gray-500 mt-1">{campaign.description}</p>
                          <div className="flex gap-4 mt-2 text-xs text-gray-500">
                            <span>{campaign.email_count} emails</span>
                            <span>{campaign.subscriber_count} subscribers</span>
                          </div>
                        </div>
                        <span
                          className={`px-2 py-1 text-xs rounded-full ${
                            campaign.status === 'active'
                              ? 'bg-green-100 text-green-800'
                              : campaign.status === 'draft'
                              ? 'bg-gray-100 text-gray-800'
                              : 'bg-yellow-100 text-yellow-800'
                          }`}
                        >
                          {campaign.status}
                        </span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Campaign Details */}
          <div className="lg:col-span-2">
            {selectedCampaign ? (
              <div className="bg-white rounded-lg shadow">
                <div className="p-6 border-b border-gray-200 flex justify-between items-center">
                  <h2 className="text-lg font-semibold text-gray-900">Campaign Emails</h2>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setShowEmailModal(true)}
                      className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm"
                    >
                      + Add Email
                    </button>
                    {campaigns.find(c => c.id === selectedCampaign)?.status === 'draft' && (
                      <button
                        onClick={() => updateCampaignStatus(selectedCampaign, 'active')}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm"
                      >
                        Activate
                      </button>
                    )}
                    {campaigns.find(c => c.id === selectedCampaign)?.status === 'active' && (
                      <button
                        onClick={() => updateCampaignStatus(selectedCampaign, 'paused')}
                        className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 text-sm"
                      >
                        Pause
                      </button>
                    )}
                    <button
                      onClick={() => deleteCampaign(selectedCampaign)}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm"
                    >
                      Delete
                    </button>
                  </div>
                </div>
                <div className="divide-y divide-gray-200">
                  {campaignEmails.length === 0 ? (
                    <div className="p-8 text-center text-gray-500">
                      <p>No emails in this campaign yet</p>
                      <button
                        onClick={() => setShowEmailModal(true)}
                        className="mt-4 text-indigo-600 hover:text-indigo-700 font-medium"
                      >
                        Add first email
                      </button>
                    </div>
                  ) : (
                    campaignEmails
                      .sort((a, b) => a.sequence_order - b.sequence_order)
                      .map((email) => (
                        <div key={email.id} className="p-6">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-3">
                                <span className="flex-shrink-0 w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center font-semibold text-sm">
                                  {email.sequence_order}
                                </span>
                                <div>
                                  <h3 className="font-medium text-gray-900">{email.subject}</h3>
                                  <p className="text-sm text-gray-500 mt-1">
                                    Send {email.delay_days > 0 && `${email.delay_days} day${email.delay_days > 1 ? 's' : ''}`}
                                    {email.delay_hours > 0 && ` ${email.delay_hours} hour${email.delay_hours > 1 ? 's' : ''}`}
                                    {email.delay_days === 0 && email.delay_hours === 0 && 'immediately'} after trigger
                                  </p>
                                </div>
                              </div>
                              <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                                <div dangerouslySetInnerHTML={{ __html: email.content }} />
                              </div>
                            </div>
                            <div className="ml-4">
                              <span
                                className={`px-2 py-1 text-xs rounded-full ${
                                  email.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                                }`}
                              >
                                {email.is_active ? 'Active' : 'Inactive'}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))
                  )}
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow p-12 text-center text-gray-500">
                <svg
                  className="mx-auto h-12 w-12 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
                <p className="mt-4">Select a campaign to view details</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* New Campaign Modal */}
      {showNewCampaignModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">Create New Campaign</h2>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Campaign Name</label>
                <input
                  type="text"
                  value={newCampaign.name}
                  onChange={(e) => setNewCampaign({ ...newCampaign, name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  placeholder="e.g., Welcome Series"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                <textarea
                  value={newCampaign.description}
                  onChange={(e) => setNewCampaign({ ...newCampaign, description: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  rows={3}
                  placeholder="Brief description of this campaign"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Trigger Type</label>
                <select
                  value={newCampaign.triggerType}
                  onChange={(e) => setNewCampaign({ ...newCampaign, triggerType: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="user_signup">User Signup</option>
                  <option value="tier_upgrade">Tier Upgrade</option>
                  <option value="course_enrolled">Course Enrolled</option>
                  <option value="course_completed">Course Completed</option>
                  <option value="inactive_7_days">Inactive 7 Days</option>
                  <option value="abandoned_cart">Abandoned Cart</option>
                  <option value="manual">Manual Trigger</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Target Tier</label>
                <select
                  value={newCampaign.targetTier}
                  onChange={(e) => setNewCampaign({ ...newCampaign, targetTier: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="all">All Users</option>
                  <option value="free">Free Users</option>
                  <option value="basic">Basic Users</option>
                  <option value="premium">Premium Users</option>
                </select>
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => setShowNewCampaignModal(false)}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={createCampaign}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
              >
                Create Campaign
              </button>
            </div>
          </div>
        </div>
      )}

      {/* New Email Modal */}
      {showEmailModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">Add Email to Campaign</h2>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Sequence #</label>
                  <input
                    type="number"
                    value={newEmail.sequenceOrder}
                    onChange={(e) => setNewEmail({ ...newEmail, sequenceOrder: parseInt(e.target.value) })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    min="1"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Delay (Days)</label>
                  <input
                    type="number"
                    value={newEmail.delayDays}
                    onChange={(e) => setNewEmail({ ...newEmail, delayDays: parseInt(e.target.value) })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    min="0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Delay (Hours)</label>
                  <input
                    type="number"
                    value={newEmail.delayHours}
                    onChange={(e) => setNewEmail({ ...newEmail, delayHours: parseInt(e.target.value) })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    min="0"
                    max="23"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Subject Line</label>
                <input
                  type="text"
                  value={newEmail.subject}
                  onChange={(e) => setNewEmail({ ...newEmail, subject: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  placeholder="e.g., Welcome to Templimize AI!"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email Content (HTML supported)</label>
                <textarea
                  value={newEmail.content}
                  onChange={(e) => setNewEmail({ ...newEmail, content: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 font-mono text-sm"
                  rows={12}
                  placeholder="<p>Hi {name},</p><p>Welcome to our platform...</p>"
                />
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => setShowEmailModal(false)}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={addEmailToCampaign}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
              >
                Add Email
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Help Modal */}
      <HelpModal
        isOpen={showHelpModal}
        onClose={() => setShowHelpModal(false)}
        title={helpGuides.dripCampaigns.title}
        steps={helpGuides.dripCampaigns.steps}
      />
    </div>
  );
};

export default DripCampaigns;
